<template>
  <div class="product-modal">
    <ProductModalContent
      v-if="product"
      :product="product"
      :hide-actions="data.withActions === false"
      @close="close"
      @promotion-click="showPromotionModal"
      @navigate="close"
    />

    <template v-else-if="isLoading">
      <div class="empty">
        <div class="empty__spinners">
          <b-spinner variant="success" small type="grow" />
          <b-spinner variant="success" small type="grow" />
          <b-spinner variant="success" small type="grow" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { eventBus } from '@/helpers/eventBus'
import { MARKET_RELATED_ROUTE_NAMES, SELLER_PRODUCT_STATUS } from '@/constants'
import { RR } from '@/plugins/retailRocket'
import ProductModalContent from '@/components/modals/product/ProductModalContent.vue'
import { ECOMMERCE_ACTION_TYPE, pushEcommerce, sendMetrics } from '@/utils/metrics'

export default {
  name: 'ProductMoreInfo',
  components: {
    ProductModalContent,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      product: null,
    }
  },
  computed: {
    ...mapState('user', ['currentUser']),
    ...mapGetters('user', ['isSeller', 'isSystemSeller']),
    ...mapGetters('auth', ['loggedIn']),
    ...mapState('profileProduct', ['sellerProducts']),
  },
  async created() {
    this.isLoading = true

    try {
      const productId = this.extractProductId(this.data.productSlug)
      const product = await this.$store.dispatch('profileProduct/loadSellerProduct', { id: productId })

      if (!this.isSystemSeller) {
        const isExpired = new Date(product.expire_at) < new Date()
        const isPublished = product.status === SELLER_PRODUCT_STATUS.publish

        if (
          !this.data.withActions === false &&
          (!isPublished || isExpired) &&
          !this.currentUserIsProductOwner(product)
        ) {
          throw new Error(this.$t('Not found'))
        }
      }

      const withCorrectSeller = {
        ...product,
        seller: product.supplier !== null ? product.supplier : product.seller,
      }

      this.product = withCorrectSeller

      this.$emit('product-loaded', withCorrectSeller)

      if (MARKET_RELATED_ROUTE_NAMES.includes(this.$route.name)) {
        RR.addProductViewTracker(this.product.id)

        sendMetrics('product_detail_view', {
          product_name: `${this.product.product.name} ${this.product.sort?.name}`,
          product_id: this.product.id,
        })

        pushEcommerce({
          actionType: ECOMMERCE_ACTION_TYPE.detail,
          products: [this.product],
        })
      }
    } catch (e) {
      eventBus.$emit('error', e)
      this.$emit('error')
      this.close()
    } finally {
      this.isLoading = false
    }
  },
  mounted() {
    window.addEventListener('popstate', this.close)
  },
  beforeDestroy() {
    window.removeEventListener('popstate', this.close)

    const state = window.history.state

    if (state && state.previousUrl) {
      window.history.replaceState(null, '', state.previousUrl)
    }
  },
  methods: {
    currentUserIsProductOwner(product) {
      return [product.seller.user.id, product.supplier?.user.id].includes(this.currentUser.id)
    },
    showPromotionModal() {
      this.close()
      eventBus.$emit('showProductPromotion', { product: this.product })
    },
    extractProductId(slug) {
      const parts = slug.split('-')
      const id = parts[parts.length - 1]

      return parseInt(id, 10)
    },
    close() {
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/functions';
@import '@/assets/scss/mixins';
@import '~bootstrap/scss/variables';
@import '~bootstrap/scss/mixins';
@import '@/assets/scss/silence/scroll-bar-decorative';

.product-modal {
  margin: $base-grid * -4;
  min-width: $base-grid * 35;
  max-width: $base-grid * 90;
  width: 90vw;

  @include media-breakpoint-down(xs) {
    max-height: none;
    margin: $base-grid * -3;
  }

  .empty {
    grid-column: 1/ -1;
    grid-row: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 500px;

    &__spinners {
      display: grid;
      grid-auto-flow: column;
      gap: 8px;
    }
  }
}
</style>
